import React from 'react'
import {
    CalendarBlank,
    Check,
    MagnifyingGlass,
    Plus,
    Gear,
    X,
    Info,
    DotsThree,
    ArrowUpRight,
    WarningCircle,
    CheckSquare,
    ArrowSquareIn,
    Funnel,
    Eye,
    Export,
    DownloadSimple,
    SortAscending,
    DotsThreeOutline,
    Copy,
    EyeClosed,
    XCircle,
    CaretUp,
    CaretDown,
    CaretRight,
    CaretLeft,
    DotsThreeVertical,
    ClockCountdown,
    Clock,
    Star,
    ArrowUDownLeft,
    PencilLine,
    PencilSimple,
    PencilCircle,
    Prohibit,
    ArrowsDownUp,
    ListNumbers,
    SortDescending,
    Warning,
    Minus,
    BookmarkSimple,
    Question,
    Image,
    ListBullets,
    User,
    ChatText,
    ShoppingCart,
    InstagramLogo,
    FacebookLogo,
    TiktokLogo,
    YoutubeLogo,
    TwitterLogo,
    CalendarCheck,
    UsersThree,
    DotsSixVertical,
    Calendar,
    Dot,
    CheckCircle,
    Handshake,
    HandCoins,
    FileText,
    HandsClapping,
    VideoCamera,
    Note,
    Heart,
    GlobeSimple,
    Hash,
    At,
    CellSignalFull,
    WifiHigh,
    BatteryFull,
    PaperPlaneRight,
    CalendarDots,
    ImageSquare,
    HandDeposit,
    MagicWand,
    CalendarPlus,
    FilePdf,
    CreditCard,
    Bank,
    Circle,
    FileArrowUp,
} from '@phosphor-icons/react'
import LoadingSpinner from './Custom/LoadingSpinner'
import CommentPlus from './Custom/CommentPlus'
import BigDot from './Custom/BigDot'
import Sparkles from './Custom/Sparkles'
import ThreadsLogo from './Custom/ThreadsLogo'
import XiaohongshuLogo from './Custom/XiaohongshuLogo'
import { SemanticColors } from '../colors'
import { useTheme } from '../themes/styled'
import { AppStoreLogo } from 'phosphor-react'

export type IconSize = '40px' | '36px' | '24px' | '20px' | '12px' | '16px' | '8px'
export type IconForm = 'regular' | 'fill'
// Added currentColor as an option to be able to change icon color via css
export type IconColor = keyof SemanticColors['icon'] | keyof SemanticColors['indicator'] | 'currentColor'
export type IndicatorColor = keyof SemanticColors['indicator']
// Icon name to component map
export const iconsMap = {
    calendarBlank: CalendarBlank,
    check: Check,
    checkCircle: CheckCircle,
    magnifyingGlass: MagnifyingGlass,
    plus: Plus,
    gear: Gear,
    x: X,
    info: Info,
    dotsThree: DotsThree,
    dotsThreeVertical: DotsThreeVertical,
    arrowUpRight: ArrowUpRight,
    warningCircle: WarningCircle,
    checkSquare: CheckSquare,
    arrowSquareIn: ArrowSquareIn,
    arrowUDownLeft: ArrowUDownLeft,
    funnel: Funnel,
    eye: Eye,
    export: Export,
    downloadSimple: DownloadSimple,
    sortAscending: SortAscending,
    dotsThreeOutline: DotsThreeOutline,
    copy: Copy,
    eyeClosed: EyeClosed,
    xCircle: XCircle,
    caretUp: CaretUp,
    caretDown: CaretDown,
    caretRight: CaretRight,
    caretLeft: CaretLeft,
    clockCountdown: ClockCountdown,
    clock: Clock,
    star: Star,
    pencilLine: PencilLine,
    pencilSimple: PencilSimple,
    pencilCircle: PencilCircle,
    prohibit: Prohibit,
    loadingSpinner: LoadingSpinner,
    arrowsDownUp: ArrowsDownUp,
    listNumbers: ListNumbers,
    sortDescending: SortDescending,
    warning: Warning,
    minus: Minus,
    bookmarkSimple: BookmarkSimple,
    commentPlus: CommentPlus,
    bigDot: BigDot,
    question: Question,
    image: Image,
    listBullets: ListBullets,
    user: User,
    chatText: ChatText,
    shoppingCart: ShoppingCart,
    instagramLogo: InstagramLogo,
    facebookLogo: FacebookLogo,
    tiktokLogo: TiktokLogo,
    youtubeLogo: YoutubeLogo,
    twitterLogo: TwitterLogo,
    calendarCheck: CalendarCheck,
    usersThree: UsersThree,
    Handshake: Handshake,
    HandCoins: HandCoins,
    dotsSixVertical: DotsSixVertical,
    calendar: Calendar,
    dot: Dot,
    fileText: FileText,
    handsClapping: HandsClapping,
    videoCamera: VideoCamera,
    appStore: AppStoreLogo,
    note: Note,
    heart: Heart,
    globeSimple: GlobeSimple,
    hash: Hash,
    at: At,
    cellSignalFull: CellSignalFull,
    wifiHigh: WifiHigh,
    batteryFull: BatteryFull,
    paperPlaneRight: PaperPlaneRight,
    calendarDots: CalendarDots,
    imageSquare: ImageSquare,
    handDeposit: HandDeposit,
    magicWand: MagicWand,
    calendarPlus: CalendarPlus,
    filePdf: FilePdf,
    creditCard: CreditCard,
    bank: Bank,
    sparkles: Sparkles,
    circle: Circle,
    fileArrowUp: FileArrowUp,
    threadsLogo: ThreadsLogo,
    xiaohongshuLogo: XiaohongshuLogo,
} as const
export type IconName = keyof typeof iconsMap

interface IconProps {
    name: IconName
    size?: IconSize
    form?: IconForm
    color?: IconColor
    fillColor?: IconColor
    className?: string
    indicatorColor?: IndicatorColor
}

const strokeMap: Record<IconSize, string> = {
    '40px': '2.5px',
    '36px': '2.25px',
    '24px': '1.5px',
    '20px': '1.25px',
    '16px': '1px',
    '12px': '0.75px',
    '8px': '0.75px',
}

const Icon: React.FC<IconProps> = ({
    name,
    size = '16px',
    form = 'regular',
    color = 'primary',
    indicatorColor,
    ...props
}) => {
    const theme = useTheme()
    const stroke = strokeMap[size]
    const iconColor =
        form === 'fill' && indicatorColor
            ? theme.colors.indicator[indicatorColor]
            : theme.colors.icon[color as keyof SemanticColors['icon']] ??
              theme.colors.indicator[color as keyof SemanticColors['indicator']] ??
              color // if it's currentColor, assign color to iconColor
    // phosphor icons props
    const phosphorProps = {
        weight: form,
        size,
        stroke,
        color: iconColor,
        ...props,
    }
    const Component = iconsMap[name]
    return Component ? <Component {...phosphorProps} /> : <></>
}

export default Icon
